<template>
  <v-responsive v-if="postHasMedia" class="post-media-container">
    <v-lazy v-model="mediaLazyState" :options="lazyOptions" :min-height="mediaLazyState ? null : 400">
      <section v-if="processedImages.length" :key="keyForRefreshingCachedMedia">
        <post-image-wrapper v-if="post.links.image && !compact" :post-id="post.id" :image-link="disabled ? null : post.links.image" class="post-image-wrapper">
          <v-img eager :src="getImageSrc(processedImages[0])" />
        </post-image-wrapper>
        <image-viewer
          v-else
          ref="imageViewer"
          :changing-image-measurements-delayed="changingImageMeasurementsDelayed"
          :class="{ 'cursor-pointer': !disabled }"
          :images="compact ? [processedImages[0]] : processedImages"
          @image-click="selectedImageIndex = $event.selectedImageIndex"
        />
        <image-viewer-modal
          v-if="selectedImageIndex !== null && !disabled"
          :images="processedImages"
          :initial-image-index="selectedImageIndex"
          @close="selectedImageIndex = null"
        />
      </section>
      <section v-if="processedVideos.length" class="fill-height">
        <video-player v-if="!disabled" :video="processedVideos[0]" />
        <v-img v-else :src="processedVideos[0].thumbnail_url" cover class="fill-height" />
      </section>
      <section v-if="post.attachments?.length">
        <div class="document-container d-flex align-center border-post-detail-15" :class="{ 'ma-4 py-2 px-4': !compact }">
          <i-custom-asset-document :class="{ 'mr-4 size-8': !compact, 'size-6': compact }"></i-custom-asset-document>
          <div v-if="!compact" class="d-flex flex-column flex-grow-1 align-start">
            <div class="font-weight-bold document-name">{{ post.attachments[0].filename }}</div>
            <div>{{ documentDescription }}</div>
          </div>
          <the-button v-if="!disabled && !compact" icon :loading="downloadingAttachment" custom-colors class="ml-4 color-post-tint" @click="downloadAttachment">
            <i-custom-download class="size-6" />
          </the-button>
        </div>
      </section>
    </v-lazy>
  </v-responsive>
</template>

<script>
import PostImageWrapper from "@/components/postables/posts/PostImageWrapper.vue"
import ImageViewer from "@/components/files/ImageViewer.vue"
import ImageViewerModal from "@/components/files/ImageViewerModal.vue"
import VideoPlayer from "@/components/files/VideoPlayer.vue"

import * as images from "@/services/images.js"
import * as postsService from "@/services/postsService.js"
import * as texts from "@/services/texts.js"

import * as postApi from "@/api/postApi.js"

export default {
  components: {
    PostImageWrapper,
    ImageViewer,
    ImageViewerModal,
    VideoPlayer
  },
  props: {
    post: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    changingImageMeasurementsDelayed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      keyForRefreshingCachedMedia: Symbol(),
      selectedImageIndex: null,
      downloadingAttachment: false,
      lazyOptions: {
        rootMargin: "1000px 100px"
      },
      mediaLazyState: false
    }
  },
  computed: {
    processedVideos() {
      return postsService.getProcessedVideos(this.post)
    },
    processedImages() {
      return postsService.getProcessedImages(this.post)
    },
    postHasMedia() {
      //Remove attachments? check when backend is fixed, here and in the template
      return this.processedImages.length || this.processedVideos.length || this.post.attachments?.length
    },
    documentDescription() {
      return texts.mimeTypeToDescription(this.post.attachments[0].mime_type)
    }
  },
  activated() {
    // Post media like gifs and videos need to be rerendered in order to work correctly
    // after returning to a cached page with posts. Otherwise they freeze.
    this.refreshCachedMediaKey()
  },
  created() {
    //Similar to activated
    this.$eventBus.$on("group-content-activity-tab-open", this.refreshCachedMediaKey)
  },
  beforeUnmount() {
    this.$eventBus.$off("group-content-activity-tab-open", this.refreshCachedMediaKey)
  },
  methods: {
    refreshCachedMediaKey() {
      this.keyForRefreshingCachedMedia = Symbol()
    },
    getImageSrc(image) {
      return images.getWidestUncroppedVersion(image).url
    },
    onVisible() {
      if (this.$refs.imageViewer) {
        this.$refs.imageViewer.onVisible()
      }
    },
    async downloadAttachment() {
      try {
        this.downloadingAttachment = true
        await postApi.downloadAttachment({ url: this.post.attachments[0].download_url, name: this.post.attachments[0].filename })
      } catch (err) {
        this.$reportError(err)
      } finally {
        this.downloadingAttachment = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-window-item) {
  position: absolute;
  top: 0;
}

.document-container {
  border: 1px solid;
  border-radius: var(--rounded-app);

  .document-name {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    white-space: nowrap;
  }
}
</style>
